import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Users,
  Star,
  Calendar,
  CheckCircle,
  ArrowRight,
  RefreshCw,
  Newspaper,
  ExternalLink,
  Home,
  MapPin,
  Clock,
  Search,
  ChevronLeft,
  ChevronRight,
  Building,
  UserCheck,
  User,
  X,
  StickyNote
} from 'lucide-react';
import { getLeads } from '../../../api/lead';
import { getProperties } from '../../../api/agent';
import { getTasks, updateTask } from '../../../api/task';
import { getInspections } from '../../../api/inspection';
import { globalSearch, getRealEstateNews } from '../../../api/dashboard';
import { getCommissionStats, getMonthlyGoals, getAgentAnalytics, getAgentAnalyticsDetail } from '../../../api/kpi';
import { isAgentUser } from '../../../utils/roleAccess';
import { apiCache, cacheKeys, cachedApiCall } from '../../../lib/cache';
import moment from 'moment';
import toast from 'react-hot-toast';
import {
  MELBOURNE_TZ,
  formatMelbourneClock,
  formatMelbourneDatePart,
  melbourneHourMinute,
} from '../../../utils/melbourneTime';
import { formatAddress, formatShortAddress, joinStreetAndSuburb } from '../../../utils/addressFormatter';
import { formatDateAU } from '../../../utils/dateFormat';
import WhoToCallPanel from '../components/WhoToCallPanel';
import SentContractsPanel from '../components/SentContractsPanel';
import MarketSignalAlertsPanel from '../components/MarketSignalAlertsPanel';
import FutureRemindersModal from '../components/FutureRemindersModal';
import LeadSourceBadge from '../components/LeadSourceBadge';
import AddSellerLeadModal from '../../leads/components/AddSellerLeadModal';
import AddBuyerModal from '../../contacts/components/AddBuyerModal';
import LeadNotesModal from '../../leads/components/LeadNotesModal';
import AgencyScopeSwitch from "../../../components/common/AgencyScopeSwitch";
import { PROSPECT_STAGES, SOLD_WINDOW_DAYS, loadProspectsPipeline } from '../../prospects/prospectsPipeline';
import AgentScopePicker, { useAgentScope } from '../../prospects/components/AgentScopePicker';
import { FUNNEL_DRILLS, funnelDrillAgents, funnelRequestFor, ownPipelineLabel, sumFunnelRows } from '../pipelineFunnel';
import { DetailModal } from '../../analytics/components/AgentAnalyticsPanel';

// One "My Performance" tile (dashboard-mockup.html): coloured icon, value +
// label, a sub-line, a bar scaled to the team's top value for that metric, and
// the agent's Rank #N for it (teal when they lead the team). When onClick is
// given the whole tile becomes a keyboard-accessible button that drills into the
// agent's full breakdown for that metric (CRM-2026-00046 - Chris Vrantsis wanted
// each tile to open his stats).
// $1,725,000 -> "$1.73M"; $620,000 -> "$620k".
const fmtGci = (n) => {
  const val = Number(n || 0);
  // parseFloat, not /\.?0+$/: that regex turned an exact $10M into "$1M".
  if (val >= 1_000_000) return `$${parseFloat((val / 1_000_000).toFixed(2))}M`;
  if (val >= 1000) return `$${Math.round(val / 1000)}k`;
  return `$${val.toLocaleString()}`;
};

const MyPerfTile = ({ icon, color, value, display, label, sub, rank, max, target, alert, onClick }) => {
  const v = Number(value || 0);
  const hasTarget = Number(target) > 0;
  const targetPct = hasTarget ? Math.min(100, Math.round((v / target) * 100)) : 0;
  const pct = hasTarget ? Math.max(3, targetPct) : (max > 0 ? Math.max(4, Math.min(100, Math.round((v / max) * 100))) : 0);
  const showBar = hasTarget || max > 0;
  const clickable = typeof onClick === "function";
  return (
    <div
      onClick={onClick}
      onKeyDown={clickable ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); } } : undefined}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      className={`bg-white rounded-[10px] border p-3 sm:p-4 ${alert ? "border-red-300 bg-red-50/40" : "border-gray-200"} ${clickable ? "cursor-pointer transition hover:border-[#128f89] hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-[#128f89]" : ""}`}
    >
      <div className="flex items-center gap-2.5 mb-2">
        <div className="w-[26px] h-[26px] rounded-[7px] flex items-center justify-center text-white text-[13px] flex-shrink-0" style={{ background: color }}>
          {icon}
        </div>
        <div>
          <div className="text-lg sm:text-xl font-extrabold text-gray-900 leading-none tabular-nums">{display != null ? display : v.toLocaleString()}</div>
          <div className="text-[11px] font-semibold text-gray-500">{label}</div>
        </div>
      </div>
      <div className={`text-[10.5px] min-h-[14px] ${alert ? "text-red-600 font-semibold" : "text-gray-400"}`}>{sub || " "}</div>
      {showBar && (
        <div className="h-[5px] bg-gray-200 rounded-full overflow-hidden mt-2.5">
          <div className="h-full rounded-full" style={{ width: `${pct}%`, background: color }} />
        </div>
      )}
      {hasTarget ? (
        <div className="mt-1.5 text-[10.5px] font-semibold text-gray-400">{targetPct}% of {typeof display === "string" && display.startsWith("$") ? fmtGci(target) : Number(target).toLocaleString()} target</div>
      ) : rank ? (
        <div className={`mt-1.5 text-[10.5px] font-semibold ${rank === 1 ? "text-[#0d6e6a]" : "text-gray-400"}`}>Rank #{rank}</div>
      ) : null}
    </div>
  );
};

// A compact one-line pipeline: "18 received → 16 contacted → 5 appraisals → …"
// (#12984250311). Absolute counts with arrows between stages.
// Each number opens the records behind it when the stage carries an onClick
// (DOMA 13343551930: "Make the numbers clickable to see which leads").
const MyPerfFunnel = ({ label, color, stages }) => (
  <div className="bg-white rounded-[10px] border border-gray-200 p-3 sm:p-4">
    <div className="text-[11px] font-bold text-gray-500 mb-2">{label}</div>
    <div className="flex items-center gap-1.5 flex-wrap">
      {stages.map((s, i) => (
        <React.Fragment key={i}>
          {s.onClick ? (
            <button
              type="button"
              onClick={s.onClick}
              title={`Show the ${s.label}`}
              className="inline-flex items-baseline gap-1 rounded px-0.5 -mx-0.5 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-[#55C5D0]"
            >
              <b className="text-sm font-extrabold tabular-nums underline decoration-dotted underline-offset-2" style={{ color }}>{Number(s.n || 0).toLocaleString()}</b>
              <span className="text-[11px] text-gray-500">{s.label}</span>
            </button>
          ) : (
          <span className="inline-flex items-baseline gap-1">
            <b className="text-sm font-extrabold tabular-nums" style={{ color }}>{Number(s.n || 0).toLocaleString()}</b>
            <span className="text-[11px] text-gray-500">{s.label}</span>
          </span>
          )}
          {i < stages.length - 1 && <ChevronRight className="w-3.5 h-3.5 text-gray-300 flex-shrink-0" />}
        </React.Fragment>
      ))}
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Dashboard redesign — everything above the Calendar (Christian Gravias, Monday
// 12988442847, final design "Collings CRM — Dashboard & Contact Profiles").
// Christian's 24 Sep note pins the scope: "THIS DESIGN IS ONLY CHANGING THE TOP
// HALF (ABOVE THE CALENDER) ON THE HOME PAGE — PLEASE KEEP THE CALENDAR & BELOW
// THE SAME", so the Calendar, Tasks & Reminders, Current Listings and Real
// Estate News rows below are untouched.
// ---------------------------------------------------------------------------

// The one stage model the design asks for, shared with the Prospects board:
// "the same five words used everywhere in the product, so an agent never has to
// translate between 'Seller Lead', 'Off Market Pipeline' and 'Appraisal'
// meaning the same thing." It lives in features/prospects/prospectsPipeline.js
// with the loader that counts it, and each card here opens the Prospects list
// on its own stage (Christian Gravias, Monday 12988442847).
const PIPELINE_STAGES = PROSPECT_STAGES.map((stage) => ({
  ...stage,
  to: `/prospects?view=list&stage=${stage.id}`,
}));

// One card in the Pipeline Snapshot strip. Coloured top border per stage, count,
// and a sub-line that carries velocity rather than just a total.
const StageCard = ({ label, count, color, foot, footAlert, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    style={{ borderTopColor: color }}
    className="text-left bg-white border border-gray-200 border-t-[3px] rounded-xl px-3.5 py-3 transition hover:-translate-y-0.5 hover:shadow-md hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#55C5D0]"
  >
    <div className="text-[11px] font-extrabold uppercase tracking-[0.08em] text-gray-600">{label}</div>
    <div className="text-2xl sm:text-[26px] font-extrabold my-1.5 tabular-nums text-gray-900">{Number(count || 0).toLocaleString()}</div>
    <div className="flex items-center justify-between gap-2">
      <span className={`text-[10.5px] ${footAlert ? 'text-red-600 font-bold' : 'text-gray-400'}`}>{foot || ' '}</span>
      <ArrowRight className="w-3.5 h-3.5 text-gray-300 flex-shrink-0" />
    </div>
  </button>
);

const DashboardPage = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);

  // State for dashboard data
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const searchTimeoutRef = useRef(null);
  const searchRef = useRef(null);
  const [gciProgress, setGciProgress] = useState({ current: 0, pipeline: 0, target: 1000000, hasTarget: false });
  // "My Performance" snapshot — the current agent's own row + rank + team totals,
  // via ?mine=1 (CRM-2026 #5, Phase 2). Only agents have a ranked row, so the
  // section is fetched/rendered for agents only and hides itself when empty.
  const [myPerf, setMyPerf] = useState(null);
  // The Collings Leads / Own Pipeline strips when the picker is on someone
  // other than the viewer (one agent's row, or a group's rows added up).
  const [funnelPerf, setFunnelPerf] = useState(null);
  const [buyerEnquiries, setBuyerEnquiries] = useState([]);
  // Seller leads waiting to be worked, split into the two buckets Christian asked
  // for (Monday 12817261831, 17 Aug 2026). These widgets used to list appraisal
  // PROPERTIES already sitting in the pipeline; they now list the raw seller leads
  // that arrive from Meta / REA / Domain, which is what "add it as an appraisal"
  // on the Seller Leads page acts on.
  const [offMarketLeads, setOffMarketLeads] = useState([]);
  const [onMarketLeads, setOnMarketLeads] = useState([]);
  // Totals from the API, not results.length. Each widget only fetches the first
  // page, so counting the rows it happens to hold caps every heading at 10 and
  // tells an admin there are 10 leads when there are 40. Same complaint class as
  // the pipeline tiles reading 0 (Christian Gravias, Monday 12817286452).
  const [leadTotals, setLeadTotals] = useState({ enquiries: 0, off_market: 0, on_market: 0 });
  // Pipeline Snapshot — one count per stage in PIPELINE_STAGES, plus the two
  // figures the design puts on the New column and the "Needs attention" tile.
  const [pipeline, setPipeline] = useState({ counts: {}, over24h: 0, total: 0 });
  const [addLeadMarket, setAddLeadMarket] = useState(null); // 'off_market' | 'on_market' | null
  const [showAddBuyer, setShowAddBuyer] = useState(false);
  // The seller lead whose notes thread is open. An agent gets handed a lead here
  // and rings it straight away, so the note has to be writable on the dashboard
  // rather than only after the lead becomes an appraisal or a Database record
  // (Christian Gravias, 2026-08-21).
  const [notesLead, setNotesLead] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [reaCurrentListings, setReaCurrentListings] = useState([]);
  const [inspections, setInspections] = useState([]);
  const [showFutureReminders, setShowFutureReminders] = useState(false);

  // The cold/warm/hot appraisal split is no longer rendered — the redesign
  // replaced the two temperature blocks with the single Pipeline Snapshot strip
  // — but loadDashboardData still totals it to work out the forecast commission
  // sitting in the pipeline, so it stays a local there rather than state here.



  // Real Estate News — live AU property articles fetched from the backend
  // (each with its real deep-article link). Replaces the old hardcoded stub
  // whose links pointed at source homepages (Monday 12618151441).
  const [newsArticles, setNewsArticles] = useState([]);

  // Calendar setup
  const [calendarView, setCalendarView] = useState('Week');

  const getWeekDays = () => {
    const today = new Date();
    const day = today.getDay(); // 0 is Sunday
    const diff = today.getDate() - day + (day === 0 ? -6 : 1); // adjust when day is sunday
    const monday = new Date(today.setDate(diff));

    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(monday);
      d.setDate(monday.getDate() + i);
      return {
        day: d.toLocaleDateString('en-US', { weekday: 'short' }),
        date: d.getDate(),
        fullDate: d.toISOString().split('T')[0]
      };
    });
  };

  const weekDays = getWeekDays();

  const getMonthDays = () => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    const days = [];

    // Fill leading empty slots
    const firstDayOfWeek = firstDay.getDay();
    for (let i = 0; i < (firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1); i++) {
      days.push(null);
    }

    // Fill days of month
    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push(i);
    }

    return days;
  };

  const monthDays = getMonthDays();

  const loadTasksData = async () => {
    try {
      // Only today's (and overdue) items — a reminder set for a future date must
      // NOT appear here until its day arrives. Upcoming ones live behind the
      // "Future Reminders" calendar button. due='today' is bucketed by the
      // agency's local day on the backend.
      const response = await getTasks({ completed: false, due: 'today' });
      const taskData = Array.isArray(response.data) ? response.data : (response.data?.results || []);

      // Sort tasks by due date or reminder date
      const sortedTasks = [...taskData].sort((a, b) => {
        const dateA = new Date(a.due_date || a.reminder_date || '9999-12-31');
        const dateB = new Date(b.due_date || b.reminder_date || '9999-12-31');
        return dateA - dateB;
      });

      setTasks(sortedTasks);
    } catch (error) {
      console.error('Error loading tasks:', error);
    }
  };

  const loadInspections = async () => {
    try {
      const today = moment().startOf('day');
      const endOfWeek = moment().endOf('week');

      console.log('Loading inspections for dashboard:', {
        date_from: today.toISOString(),
        date_to: endOfWeek.toISOString()
      });

      const response = await cachedApiCall(
        'dashboard_inspections',
        () => getInspections({
          date_from: today.toISOString(),
          date_to: endOfWeek.toISOString(),
          status: 'scheduled',
          page_size: 20
        }),
        2 * 60 * 1000 // 2 minutes cache
      );

      const inspectionData = response.data?.results || response.data || [];
      console.log(`Dashboard loaded ${inspectionData.length} inspections`);
      setInspections(inspectionData);
    } catch (error) {
      console.error('Error loading inspections for dashboard:', error);
      console.error('Error response:', error.response);
      console.error('Error status:', error.response?.status);
      setInspections([]);
    }
  };

  const loadNews = async () => {
    try {
      // Cached 30 min on top of the backend's own hourly refresh — the news
      // rarely changes and this keeps the dashboard load light.
      const response = await cachedApiCall(
        'dashboard_news',
        () => getRealEstateNews(),
        30 * 60 * 1000
      );
      setNewsArticles(response.data?.results || []);
    } catch (error) {
      console.error('Error loading real estate news for dashboard:', error);
      setNewsArticles([]);
    }
  };

  // Load Leads Data (Recent Buyer Enquiries – only buyer enquiry type; pipelines show appraisals)
  // NOTE: No caching here — always fetch fresh so the dashboard count is never stale.
  const loadBuyerEnquiries = async () => {
    try {
      // Invalidate any leftover cache so stale data is not served elsewhere
      apiCache.invalidate('dashboard_buyer_enquiries');
      
      // Fetch both buyer enquiries and offers in parallel to show both in the dashboard list
      // Scoped by the dashboard's own All agents / My records switch, the same
      // one the appraisal tiles read. Left unscoped, these two widgets went on
      // showing every agent's enquiries after the tiles beside them had been
      // narrowed to the reader's own (Simon Abbott, 3 Sep 2026).
      const scope = { agency_wide: pipelineAgencyWide ? 'true' : 'false' };
      const [newLeadsRes, offersRes] = await Promise.all([
        getLeads({ type: 'buyer_enquiry', page_size: 10, ...scope }),
        getLeads({ type: 'offer', page_size: 10, ...scope })
      ]);
      
      const enquiries = (newLeadsRes.data?.results || []).map(l => ({ ...l, lead_type: l.lead_type || 'buyer_enquiry' }));
      const offers = (offersRes.data?.results || []).map(l => ({ ...l, lead_type: l.lead_type || 'offer' }));
      
      // Combine and sort by created_at descending (most recent first)
      const combined = [...enquiries, ...offers].sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      );
      
      setBuyerEnquiries(combined.slice(0, 10));
      setLeadTotals((prev) => ({
        ...prev,
        enquiries: (newLeadsRes.data?.count ?? enquiries.length) + (offersRes.data?.count ?? offers.length),
      }));
    } catch (error) {
      console.error('Error loading buyer enquiries and offers:', error);
    }
  };

  // Seller leads for the two blue widgets. type=seller is the clean owner/seller
  // filter added for this ticket — the older type=off_market also matches the
  // legacy lead_type='off_market' marker, which is a buyer enquiry that has been
  // attached to a property, so it would put buyers in a seller list.
  const loadSellerLeads = async () => {
    try {
      const scope = { agency_wide: pipelineAgencyWide ? 'true' : 'false' };
      const [offRes, onRes] = await Promise.all([
        getLeads({ type: 'seller', market: 'off_market', page_size: 10, ...scope }),
        getLeads({ type: 'seller', market: 'on_market', page_size: 10, ...scope })
      ]);
      const offResults = offRes.data?.results || [];
      const onResults = onRes.data?.results || [];
      setOffMarketLeads(offResults);
      setOnMarketLeads(onResults);
      setLeadTotals((prev) => ({
        ...prev,
        off_market: offRes.data?.count ?? offResults.length,
        on_market: onRes.data?.count ?? onResults.length,
      }));
    } catch (error) {
      console.error('Error loading seller leads:', error);
      setOffMarketLeads([]);
      setOnMarketLeads([]);
      setLeadTotals((prev) => ({ ...prev, off_market: 0, on_market: 0 }));
    }
  };

  // Pipeline Snapshot counts, from the Prospects board's own loader. Each stage
  // is read off the record the CRM keeps for it (holding-area seller leads
  // split by whether the agent pressed Called, the appraisal boards, live
  // listings, recent sales), not off Lead.stage, which nothing in the seller
  // workflow moves after a lead arrives. page_size 1 because only the counts
  // are wanted here; each one is the server's own total.
  const loadPipelineSnapshot = async () => {
    try {
      const data = await loadProspectsPipeline({
        agencyWide: pipelineAgencyWide,
        canScope: canSwitchPipelineScope,
        boardAgent: pipelineScope.boardAgentId,
        team: pipelineScope.team,
        ownOnly: pipelineScope.ownOnly,
        pageSize: 1,
      });
      setPipeline({ counts: data.counts, over24h: data.atRisk, total: data.total });
    } catch (error) {
      console.error('Error loading pipeline snapshot:', error);
    }
  };

  // "New Seller Leads" — the off-market and on-market lists shown as one, newest
  // first, each row keeping its own market so the Notes modal and the "View all"
  // link still land on the right tab. Both lists are already fetched; this only
  // decides the order they are read in.
  const newSellerLeads = [
    ...offMarketLeads.map((l) => ({ ...l, market: 'off_market' })),
    ...onMarketLeads.map((l) => ({ ...l, market: 'on_market' })),
  ]
    .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))
    .slice(0, 10);

  const noteCountOf = (lead) => (Array.isArray(lead?.agent_notes) ? lead.agent_notes.length : 0);

  // Writing a note never actions the lead, so the row stays in its widget. Both
  // lists are patched because a lead only ever sits in one of them and this saves
  // knowing which.
  const handleLeadNoteSaved = (updated) => {
    const patch = (list) =>
      list.map((l) => (l.id === updated.id ? { ...l, agent_notes: updated.agent_notes } : l));
    setOffMarketLeads(patch);
    setOnMarketLeads(patch);
    setNotesLead((prev) => (prev && prev.id === updated.id ? { ...prev, agent_notes: updated.agent_notes } : prev));
  };



  // Load Real Estate Data
  const loadRealEstateData = async () => {
    try {
      // Source from the CRM "My Listings" endpoint so this widget matches what's
      // under the Listings tab. The REA-portal endpoint (getCollingsProperties)
      // also surfaces sold/rental records the admin adds for portal syndication,
      // which don't belong here.
      const res = await getProperties({ page: 1, page_size: 30 });
      const data = res.data;
      const allResults = Array.isArray(data) ? data : (data?.results || []);

      const forSale = allResults.filter((prop) => {
        const statusText = (prop.status?.name || prop.status || '').toLowerCase();
        const isSold = prop.is_sold || statusText === 'sold';
        if (isSold || prop.is_withdrawn) return false;

        // Exclude rentals — the widget is "current listings for sale".
        const transType = (prop.transaction_type || '').toLowerCase();
        const propType = (prop.property_type || '').toLowerCase();
        const isLease = transType === 'lease' || propType.includes('lease') || propType.includes('rent');
        if (isLease) return false;

        // Exclude appraisals and Buyer-Portal "sell your property" placeholders.
        if (statusText.includes('appraisal')) return false;
        const description = (prop.description || '').toLowerCase();
        if (description.includes('sell your property lead')) return false;

        return true;
      });

      const listings = forSale.map((prop) => {
        const daysOnMarket = prop.created_at
          ? Math.floor((new Date() - new Date(prop.created_at)) / (1000 * 60 * 60 * 24))
          : 0;

        const address = formatShortAddress(prop) || prop.headline || 'Unknown Address';

        let displayImage = null;
        if (prop.images && prop.images.length > 0) {
          displayImage = typeof prop.images[0] === 'string' ? prop.images[0] : prop.images[0].file;
        }

        return {
          id: prop.id,
          address,
          days: daysOnMarket,
          price: prop.price || 'Contact Agent',
          type: prop.transaction_type || 'sale',
          bedrooms: prop.beds || prop.bedrooms,
          bathrooms: prop.baths || prop.bathrooms,
          image: displayImage,
          headline: prop.headline
        };
      });

      setReaCurrentListings(listings);
    } catch (error) {
      console.error('Error loading real estate data:', error);
      setReaCurrentListings([]);
    }
  };

  // Handle search functionality
  const handleSearch = async (query) => {
    if (!query || query.trim().length < 2) {
      setSearchResults(null);
      setShowSearchResults(false);
      return;
    }

    setIsSearching(true);
    try {
      const response = await globalSearch({ q: query.trim(), limit: 5 });
      setSearchResults(response.data);
      setShowSearchResults(true);
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults(null);
      setShowSearchResults(false);
    } finally {
      setIsSearching(false);
    }
  };

  // Debounced search handler
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (searchQuery.trim().length >= 2) {
      searchTimeoutRef.current = setTimeout(() => {
        handleSearch(searchQuery);
      }, 300);
    } else {
      setSearchResults(null);
      setShowSearchResults(false);
    }

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery]);

  // Close search results when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSearchResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Handle result click navigation
  const handleResultClick = (result) => {
    setShowSearchResults(false);
    setSearchQuery('');

    // For leads, navigate to the buyer enquiries page instead of detail page
    if (result.type === 'lead') {
      navigate('/leads');
    } else {
      // For properties and contacts, use the URL from the result
      navigate(result.url);
    }
  };

  // Format price for display
  const formatPrice = (price) => {
    if (!price || price === 0) return 'Contact Agent';
    if (price >= 1000000) {
      return `$${(price / 1000000).toFixed(1)}M`;
    }
    return `$${Math.round(price).toLocaleString()}`;
  };

  // The search dropdown is read top to bottom: the agent's listings, then the
  // people, then the Database layer underneath both. Christian Gravias,
  // CRM-2026-00031 (17 Sep 2026): "Please put the contacts on top of the public
  // properties." The backend answers the Database rows in their own bucket now;
  // the filter keeps this working against the older shape, where they came back
  // inside `properties` marked with `source: 'public'`.
  const allPropertyResults = searchResults?.properties || [];
  const publicPropertyResults =
    searchResults?.public_properties || allPropertyResults.filter((p) => p.source === 'public');
  const listingResults = allPropertyResults.filter((p) => p.source !== 'public');
  const searchResultCount =
    listingResults.length + (searchResults?.contacts?.length || 0) + publicPropertyResults.length;

  const formatGCI = (amount) => {
    if (!amount) return '$0';
    if (amount >= 1000000) return `$${(amount / 1000000).toFixed(1)}M`;
    return `$${Math.round(amount).toLocaleString()}`;
  };

  // Shares the saved 'pipeline' choice with OffMarketPipelinePage, so the tiles
  // and the board they link to can never disagree about whose appraisals these
  // are - the exact mismatch that produced "It says zero info but when I click
  // it shows the below" (Christian Gravias, Monday 12817286452).
  //
  // The super admin, and an agent in a team, can also narrow the Pipeline
  // Snapshot to one agent (Christian Gravias, Monday 12988442847, 14 Oct 2026:
  // "ON THE HOME PAGE: Add a button for me to toggle through specific
  // agents"). The same picker as the Prospects board; a tile then opens the
  // board on that agent.
  const pipelineScope = useAgentScope();
  const {
    agencyWide: pipelineAgencyWide,
    setAgencyWide: setPipelineAgencyWide,
    canSwitch: canSwitchPipelineScope,
  } = pipelineScope;

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const priorityMap = {
        low: 'cold',
        medium: 'warm',
        high: 'hot'
      };

      // Helper to parse GCI safely
      const parseGCI = (val) => {
        if (!val) return 0;
        if (typeof val === 'number') return val;
        // Remove commas, currency symbols, and whitespace
        const cleanVal = String(val).replace(/[^0-9.-]+/g, '');
        return parseFloat(cleanVal) || 0;
      };

      // Fetch On-Market and Off-Market appraisal properties (pipelines show appraisal listings, not leads)
      // No caching here — newly added appraisals must appear on the dashboard immediately.
      apiCache.invalidate('dashboard_all_onmarket');
      apiCache.invalidate('dashboard_all_offmarket');
      // The pipeline pages are a team-wide view for admins/PAs (OffMarketPipelinePage.js:29),
      // so the dashboard summary must be scoped the same way. Without agency_wide the BE
      // falls back to "listings where I am agent/co-agent/tertiary agent"
      // (property_management/views/property_listing_views.py:485-489), which showed an
      // admin 0 leads / $0 GCI on the dashboard while "View Full Pipeline" listed another
      // agent's appraisals (Christian Gravias, Monday 12817286452, 2026-08-17).
      // Must match whatever the pipeline page is currently showing, or the tile
      // reads a different number from the board it links to - that mismatch was
      // the original bug here (Christian Gravias, Monday 12817286452). The
      // pipeline is now a per-user CHOICE rather than a role side effect, so the
      // tiles read the same saved choice instead of re-deriving it from the role.
      const seesAgencyWide = pipelineAgencyWide;
      const currentYear = new Date().getFullYear();
      const currentMonth = new Date().getMonth() + 1;
      // GCI Target Progress must reset each Australian financial year (1 Jul → 30 Jun),
      // not each calendar year. Scope the banked (sold) commission to the current FY so
      // the bar starts fresh at $0 on 1 July instead of carrying Jan–Jun figures over.
      const fyStartYear = currentMonth >= 7 ? currentYear : currentYear - 1;
      const fyStartDate = `${fyStartYear}-07-01`;
      const fyEndDate = `${fyStartYear + 1}-06-30`;
      const [onMarketRes, offMarketRes, commissionStatsRes, goalsRes] = await Promise.all([
        getProperties({ appraisal_pipeline: 'on_market', page_size: 1000, agency_wide: seesAgencyWide ? true : undefined }),
        getProperties({ appraisal_pipeline: 'off_market', page_size: 1000, agency_wide: seesAgencyWide ? true : undefined }),
        getCommissionStats({ start_date: fyStartDate, end_date: fyEndDate, agent_id: user?.id }).catch((err) => {
          console.error('Error loading commission stats for GCI target:', err);
          return { data: { ytd_stats: { total_commission: 0 } } };
        }),
        // GCI target is set by the agent in the KPI Tracking tab (MonthlyGoal.commission_target)
        getMonthlyGoals().catch((err) => {
          console.error('Error loading monthly goals for GCI target:', err);
          return { data: [] };
        })
      ]);

      const onMarketList = onMarketRes.data?.results ?? (Array.isArray(onMarketRes.data) ? onMarketRes.data : []);
      const offMarketList = offMarketRes.data?.results ?? (Array.isArray(offMarketRes.data) ? offMarketRes.data : []);

      // Calculate Stats from appraisal properties (temperature = cold/warm/hot, commission from price/rate/amount)
      const newStats = {
        onMarket: { cold: { count: 0, value: 0 }, warm: { count: 0, value: 0 }, hot: { count: 0, value: 0 } },
        offMarket: { cold: { count: 0, value: 0 }, warm: { count: 0, value: 0 }, hot: { count: 0, value: 0 } }
      };

      const commissionFromProperty = (p) => {
        const price = parseFloat(p.price) || 0;
        const rate = p.commission_rate != null ? parseFloat(p.commission_rate) : null;
        const amount = p.commission_amount != null ? parseFloat(p.commission_amount) : null;
        if (amount != null && amount > 0) return amount;
        if (rate != null && rate > 0 && price > 0) return (rate / 100) * price;
        return 0;
      };

      onMarketList.forEach((prop) => {
        const tempKey = (prop.appraisal_temperature || '').toLowerCase();
        if (tempKey === 'cold' || tempKey === 'warm' || tempKey === 'hot') {
          const gci = commissionFromProperty(prop);
          newStats.onMarket[tempKey].count += 1;
          newStats.onMarket[tempKey].value += gci;
        }
      });

      offMarketList.forEach((prop) => {
        const tempKey = (prop.appraisal_temperature || '').toLowerCase();
        if (tempKey === 'cold' || tempKey === 'warm' || tempKey === 'hot') {
          const gci = commissionFromProperty(prop);
          newStats.offMarket[tempKey].count += 1;
          newStats.offMarket[tempKey].value += gci;
        }
      });

      // GCI Target Progress = banked commission from SOLD properties PLUS forecast
      // commission sitting in the pipeline (on-market + off-market appraisals).
      const soldCommission = parseGCI(commissionStatsRes?.data?.ytd_stats?.total_commission);
      const pipelineCommission = ['onMarket', 'offMarket'].reduce((sum, mkt) =>
        sum + ['cold', 'warm', 'hot'].reduce((s, temp) => s + (newStats[mkt][temp].value || 0), 0)
      , 0);

      // GCI target comes from the agent's MonthlyGoal for the current month. When
      // the agent has not set one we show "No target set" rather than a made-up
      // $1M figure (the widget read like a real goal before), but still keep a
      // non-zero denominator so the progress bar maths never divides by zero.
      const goalsList = Array.isArray(goalsRes.data) ? goalsRes.data : (goalsRes.data?.results || []);
      const monthGoal = goalsList.find(g => g.month === currentMonth && g.year === currentYear);
      const rawTarget = parseGCI(monthGoal?.commission_target);
      const hasTarget = rawTarget > 0;
      const gciTarget = hasTarget ? rawTarget : 1000000;

      setGciProgress(prev => ({ ...prev, current: soldCommission, pipeline: pipelineCommission, target: gciTarget, hasTarget }));

      setLoading(false);

    } catch (error) {
      console.error('Error loading dashboard data:', error);
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRealEstateData();
    loadTasksData();
    loadInspections();
    loadNews();
  }, []);

  // The lead widgets read the scope switch, so they belong with the tiles that
  // read it too - and, like them, have to refetch once the redux user arrives
  // and the role default settles.
  useEffect(() => {
    loadBuyerEnquiries();
    loadSellerLeads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pipelineAgencyWide]);

  // The Pipeline Snapshot loads on its own rather than at the tail of
  // loadDashboardData: it no longer needs anything that fetches, and there a
  // slow or failed call elsewhere left all five cards at 0. Same scope switch,
  // so it refetches with the tiles once the role is known.
  useEffect(() => {
    loadPipelineSnapshot();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pipelineAgencyWide, pipelineScope.boardAgentId, pipelineScope.team, pipelineScope.ownOnly]);

  // Separate from the mount effect above because it has a dependency. On a cold
  // load the redux user can still be null on first render, so the scope starts
  // false and only settles once the role is known; refetching on that change is
  // what stops a super admin from being shown 0 leads / $0 GCI for the rest of
  // the session.
  useEffect(() => {
    loadDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pipelineAgencyWide]);

  // My Performance snapshot. Scoped to the current financial year so "sold /
  // commission" line up with the GCI target tile above. Loaded for everyone
  // with a row, not just agents, because the Collings Leads / Your Own
  // Pipeline lines under the Pipeline Snapshot read it too and the principal
  // works his own pipeline (Christian Gravias, Monday 12988442847, 13 Oct
  // 2026: "add this section under the pipeline snapshot"). The My Performance
  // block itself is still agents only.
  useEffect(() => {
    if (!user?.id) { setMyPerf(null); return; }
    const now = new Date();
    const fyStartYear = (now.getMonth() + 1) >= 7 ? now.getFullYear() : now.getFullYear() - 1;
    const segment = user?.department === 'rental' ? 'rentals' : 'sales';
    getAgentAnalytics({ mine: 1, start_date: `${fyStartYear}-07-01`, end_date: `${fyStartYear + 1}-06-30`, segment })
      .then((res) => setMyPerf(res.data))
      .catch(() => setMyPerf(null));
  }, [user]);

  // The two strips under the Pipeline Snapshot follow its agent picker, like
  // the stage tiles do (DOMA 13343551930: "Update Collings leads & your
  // pipeline stats when I change agents"). A picked agent reads that agent's
  // row; All agents / Whole team adds the group's rows up; My pipeline falls
  // back to the viewer's own row above. The My Performance block stays
  // personal.
  useEffect(() => {
    const req = funnelRequestFor({
      boardAgentId: pipelineScope.boardAgentId,
      team: pipelineScope.team,
      agencyWide: pipelineAgencyWide,
      pickerMode: pipelineScope.pickerMode,
    });
    if (!user?.id || !req) { setFunnelPerf(null); return undefined; }
    let live = true;
    const now = new Date();
    const fyStartYear = (now.getMonth() + 1) >= 7 ? now.getFullYear() : now.getFullYear() - 1;
    const segment = user?.department === 'rental' ? 'rentals' : 'sales';
    getAgentAnalytics({ ...req.params, start_date: `${fyStartYear}-07-01`, end_date: `${fyStartYear + 1}-06-30`, segment })
      .then((res) => {
        if (!live) return;
        setFunnelPerf(req.kind === 'agent' ? (res.data?.agent || null) : sumFunnelRows(res.data?.agents));
      })
      .catch(() => live && setFunnelPerf(null));
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, pipelineScope.boardAgentId, pipelineScope.team, pipelineAgencyWide, pipelineScope.pickerMode]);
  const funnelScoped = Boolean(funnelRequestFor({
    boardAgentId: pipelineScope.boardAgentId,
    team: pipelineScope.team,
    agencyWide: pipelineAgencyWide,
    pickerMode: pipelineScope.pickerMode,
  }));
  // Never show the viewer's own numbers under another agent's label: while a
  // scoped request is loading (or failed) the strips wait rather than lie.
  const funnel = funnelScoped ? funnelPerf : myPerf?.agent;

  // The records behind a strip number, in the same DetailModal the Agent
  // Performance page uses. Rows come from the analytics drill endpoint for
  // whoever the picker is on (one agent, each team member, or agency-wide).
  const [funnelDrill, setFunnelDrill] = useState({ open: false, title: '', loading: false, rows: [], metric: '' });
  const openFunnelDrill = (strip, stage) => {
    const drill = FUNNEL_DRILLS[strip]?.[stage];
    if (!drill) return;
    const agents = funnelDrillAgents({
      boardAgentId: pipelineScope.boardAgentId,
      team: pipelineScope.team,
      agencyWide: pipelineAgencyWide,
      pickerMode: pipelineScope.pickerMode,
      agentOptions: pipelineScope.agentOptions,
      viewerId: user?.id,
    });
    if (!agents.length) return;
    const who = funnelScoped ? pipelineScope.scopeLabel : (user?.name || 'Me');
    const title = `${strip === 'collings' ? 'Collings leads' : 'Own pipeline'} · ${drill.title} · ${who}`;
    setFunnelDrill({ open: true, title, loading: true, rows: [], metric: drill.metric });
    const now = new Date();
    const fyStartYear = (now.getMonth() + 1) >= 7 ? now.getFullYear() : now.getFullYear() - 1;
    const segment = user?.department === 'rental' ? 'rentals' : 'sales';
    const base = { metric: drill.metric, ...(drill.params || {}), start_date: `${fyStartYear}-07-01`, end_date: `${fyStartYear + 1}-06-30`, segment };
    Promise.all(agents.map((agent) => getAgentAnalyticsDetail(agent ? { ...base, agent } : base)))
      .then((responses) => {
        const seen = new Set();
        const rows = responses
          .flatMap((res) => res.data?.results || [])
          .filter((r) => (drill.filter ? drill.filter(r) : true))
          .filter((r) => { const k = `${r.kind}-${r.id}`; if (seen.has(k)) return false; seen.add(k); return true; });
        setFunnelDrill((d) => ({ ...d, loading: false, rows }));
      })
      .catch(() => {
        toast.error('Could not load these records.');
        setFunnelDrill((d) => ({ ...d, open: false, loading: false }));
      });
  };

  return (
    <div className="w-full bg-white min-h-screen p-3 sm:p-4 md:p-6 space-y-4 sm:space-y-5 md:space-y-6">
      {/* Top row, the current CRM's layout (Christian Gravias, Monday
          12988442847, 1 + 12 Oct 2026: "On dashboard copy the style like we
          currently have it on our CRM" and "Please put this in the corner like
          we currently have it"): search with Open pipeline and Add Lead beside
          it, GCI Target Progress in the right-hand corner. The "Your day at a
          glance" header, Activity streak and the Total pipeline / Needs
          attention / Appraisals tiles were removed at his request ("remove
          everything else at the top and leave those buttons"). */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center">
        <div className="md:col-span-2 flex flex-col sm:flex-row sm:items-center gap-2">
        {/* Search Bar */}
        <div className="relative w-full flex-1 min-w-0" ref={searchRef}>
          <Search className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400 z-10" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={() => {
              if (searchResults && searchQuery.trim().length >= 2) {
                setShowSearchResults(true);
              }
            }}
            placeholder="Search Properties, Contacts..."
            className="w-full pl-10 sm:pl-12 pr-3 sm:pr-4 py-2.5 sm:py-3 border border-[#B9B9B9] rounded-lg text-xs sm:text-sm focus:outline-none focus:ring-1 focus:ring-teal-500 relative z-10"
          />

          {/* Search Results Dropdown */}
          {showSearchResults && searchResults && (
            <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-xl z-50 max-h-[500px] overflow-y-auto">
              {isSearching ? (
                <div className="p-4 text-center text-gray-500 text-sm">Searching...</div>
              ) : searchResultCount === 0 ? (
                <div className="p-4 text-center text-gray-500 text-sm">No results found</div>
              ) : (
                <div className="p-2">
                  {/* Properties Section - the agent's own appraisals and
                      listings. The Database layer sits in its own group below
                      the contacts. */}
                  {listingResults.length > 0 && (
                    <div className="mb-3">
                      <div className="px-3 py-2 text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-2">
                        <Building className="w-3 h-3" />
                        Properties ({listingResults.length})
                      </div>
                      {listingResults.map((property) => (
                        <SearchPropertyRow
                          key={`property-${property.id}`}
                          property={property}
                          onClick={handleResultClick}
                          formatPrice={formatPrice}
                        />
                      ))}
                    </div>
                  )}



                  {/* Contacts Section */}
                  {searchResults.contacts && searchResults.contacts.length > 0 && (
                    <div className="mb-3">
                      <div className="px-3 py-2 text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-2">
                        <User className="w-3 h-3" />
                        Contacts ({searchResults.contacts.length})
                      </div>
                      {searchResults.contacts.map((contact) => (
                        <div
                          key={`contact-${contact.id}`}
                          onClick={() => handleResultClick(contact)}
                          className="px-3 py-2.5 hover:bg-purple-50 rounded-lg cursor-pointer transition-colors group"
                        >
                          <div className="flex items-start gap-3">
                            <div className="mt-0.5 p-1.5 bg-purple-100 rounded group-hover:bg-purple-200 transition-colors">
                              <User className="w-4 h-4 text-purple-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="font-bold text-sm text-gray-900 group-hover:text-purple-700">
                                {contact.name || 'Unknown Contact'}
                              </div>
                              {contact.company && (
                                <div className="text-xs text-gray-500 mt-0.5">{contact.company}</div>
                              )}
                              <div className="flex items-center gap-3 mt-1 text-xs text-gray-600">
                                {contact.email && <span>{contact.email}</span>}
                                {contact.phone && <span>{contact.phone}</span>}
                                {contact.contact_type && (
                                  <span className="px-2 py-0.5 bg-purple-100 text-purple-600 rounded text-[10px] font-medium">
                                    {contact.contact_type}
                                  </span>
                                )}
                              </div>
                            </div>
                            <ArrowRight className="w-4 h-4 text-gray-300 group-hover:text-purple-500 opacity-0 group-hover:opacity-100 transition-all" />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Public Properties - the Database layer, last, under the
                      contacts. Christian Gravias, CRM-2026-00031 (17 Sep 2026):
                      "Please put the contacts on top of the public properties."
                      Still the properties family (same row, same Building icon),
                      just its own group, because a layer below appraisals and
                      listings cannot sit above the people. */}
                  {publicPropertyResults.length > 0 && (
                    <div className="mb-3">
                      <div className="px-3 py-2 text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-2">
                        <Building className="w-3 h-3" />
                        Public Properties ({publicPropertyResults.length})
                        <span className="normal-case tracking-normal font-medium text-[10px] text-gray-400">
                          from your Database
                        </span>
                      </div>
                      {publicPropertyResults.map((property) => (
                        <SearchPropertyRow
                          key={`public-property-${property.id}`}
                          property={property}
                          onClick={handleResultClick}
                          formatPrice={formatPrice}
                        />
                      ))}
                    </div>
                  )}

                  {/* View All Results */}
                  {searchResultCount > 0 && (
                    <div className="px-3 py-2 border-t border-gray-100">
                      <button
                        onClick={() => {
                          setShowSearchResults(false);
                        }}
                        className="w-full text-center text-xs font-medium text-teal-600 hover:text-teal-700 py-1"
                      >
                        View all {searchResultCount} results
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              type="button"
              onClick={() => navigate('/prospects')}
              className="min-h-[38px] px-4 rounded-lg border border-gray-200 bg-white text-gray-700 text-[12.5px] font-bold whitespace-nowrap hover:bg-gray-50 hover:border-gray-300 transition"
            >
              Open pipeline
            </button>
            <button
              type="button"
              onClick={() => setAddLeadMarket('off_market')}
              className="min-h-[38px] px-4 rounded-lg bg-[#55C5D0] border border-[#55C5D0] text-white text-[12.5px] font-bold whitespace-nowrap shadow-sm hover:bg-[#46b4bf] transition"
            >
              + Add Lead
            </button>
          </div>
        </div>

        {/* GCI Target Progress. Click to view sold-property commission breakdown. */}
        <div
          onClick={() => navigate('/commissions')}
          className="md:col-span-1 flex items-center w-full cursor-pointer group rounded-md p-1 -m-1 hover:bg-teal-50/40 transition-colors"
          title="View commission breakdown"
        >
          <div className="flex flex-col w-full">
            <div className="flex justify-between items-end mb-1">
              <span className="text-xs sm:text-sm font-bold text-gray-800 group-hover:text-teal-600 transition-colors">GCI Target Progress</span>
              <span className="text-xs sm:text-sm font-bold text-[#3ACA6F]">{formatGCI(gciProgress.current)}</span>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-[10px] sm:text-xs font-bold text-gray-800">$0</span>
              <div className="flex-1 h-3 sm:h-4 bg-gray-100 rounded-full overflow-hidden shadow-inner flex" title={gciProgress.hasTarget ? `Sold (banked): ${formatGCI(gciProgress.current)}` : 'Set a GCI target on the KPI Tracking page to track progress'}>
                <div
                  className="h-full bg-teal-500 shadow-sm transition-all duration-1000 ease-out"
                  style={{ width: `${gciProgress.hasTarget ? Math.min((gciProgress.current / gciProgress.target) * 100, 100) : 0}%` }}
                ></div>
              </div>
              <span className="text-[10px] sm:text-xs font-bold text-gray-800 whitespace-nowrap">{gciProgress.hasTarget ? `${formatGCI(gciProgress.target)} target` : 'No target set'}</span>
            </div>
            <div className="flex items-center gap-3 mt-1">
              <span className="flex items-center gap-1 text-[9px] sm:text-[10px] text-gray-500 font-medium"><span className="w-2 h-2 rounded-full bg-teal-500"></span>Sold {formatGCI(gciProgress.current)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Pipeline Snapshot — the single strip that replaces the old On-Market +
          Off-Market blocks (2 rows x 3 temperature bars). Market is a filter on
          the pipeline page itself now, so the dashboard is never a different
          mental model from the page it summarises. */}
      <div>
        <div className="flex items-center gap-2.5 flex-wrap mb-3">
          <h2 className="text-[15px] font-bold text-gray-900 m-0">Pipeline Snapshot</h2>
          <span className="text-[11px] text-gray-400">One stage model across Dashboard and Prospects</span>
          <div className="ml-auto flex items-center gap-3 flex-wrap">
            {/* Mine / All agents, kept with the numbers it scopes now that the
                header it sat in has gone (same place as on the live CRM). */}
            {pipelineScope.pickerMode ? (
              <AgentScopePicker scope={pipelineScope} />
            ) : (
              canSwitchPipelineScope && (
                <AgencyScopeSwitch
                  agencyWide={pipelineAgencyWide}
                  onChange={setPipelineAgencyWide}
                  mineLabel="Mine"
                  agencyLabel="All agents"
                />
              )
            )}
            <button
              type="button"
              onClick={() => navigate(pipelineScope.boardAgentId ? `/prospects?agent=${pipelineScope.boardAgentId}` : '/prospects')}
              className="text-[11.5px] font-extrabold text-[#167f8b] hover:underline"
            >
              View full pipeline →
            </button>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2.5">
          {PIPELINE_STAGES.map((stage) => (
            <StageCard
              key={stage.id}
              label={stage.label}
              color={stage.color}
              count={pipeline.counts[stage.id]}
              foot={
                stage.id === 'new' && pipeline.over24h > 0
                  ? `${pipeline.over24h} > 24h old`
                  : stage.id === 'sold'
                  ? `Last ${SOLD_WINDOW_DAYS} days`
                  : stage.id === 'listed'
                  ? 'Not on portals yet'
                  : stage.id === 'lost'
                  ? 'Tracked, stats kept'
                  : undefined
              }
              footAlert={stage.id === 'new'}
              onClick={() =>
                navigate(pipelineScope.boardAgentId ? `${stage.to}&agent=${pipelineScope.boardAgentId}` : stage.to)
              }
            />
          ))}
        </div>
        {/* One-line pipelines: Collings vs the agent's own (#12984250311).
            Sits under the snapshot (Christian Gravias, Monday 13177615952).
            Anyone with their own row, the principal included (Monday
            12988442847). */}
        {funnel && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mt-3">
            <MyPerfFunnel
              label="Collings Leads" color="#4b4fd6"
              stages={[
                { n: funnel.collings_leads, label: 'received', onClick: () => openFunnelDrill('collings', 'received') },
                { n: funnel.collings_contacted, label: 'contacted', onClick: () => openFunnelDrill('collings', 'contacted') },
                { n: funnel.collings_appraisals, label: 'appraisals', onClick: () => openFunnelDrill('collings', 'appraisals') },
                { n: funnel.collings_listings_won, label: 'listings', onClick: () => openFunnelDrill('collings', 'listings') },
                { n: funnel.collings_sales, label: 'sold', onClick: () => openFunnelDrill('collings', 'sold') },
                { n: funnel.collings_leads_lost, label: 'lost', onClick: () => openFunnelDrill('collings', 'lost') },
              ]}
            />
            <MyPerfFunnel
              label={funnelScoped ? ownPipelineLabel({
                boardAgentName: pipelineScope.boardAgentId ? pipelineScope.scopeLabel : '',
                team: pipelineScope.team,
                agencyWide: pipelineAgencyWide,
              }) : 'Your Own Pipeline'}
              color="#0d6e6a"
              stages={[
                { n: funnel.agent_leads, label: 'opportunities', onClick: () => openFunnelDrill('own', 'received') },
                { n: funnel.agent_appraisals, label: 'appraisals', onClick: () => openFunnelDrill('own', 'appraisals') },
                { n: funnel.agent_listings_won, label: 'listings', onClick: () => openFunnelDrill('own', 'listings') },
                { n: funnel.agent_sales, label: 'sold', onClick: () => openFunnelDrill('own', 'sold') },
                { n: funnel.agent_leads_lost, label: 'lost', onClick: () => openFunnelDrill('own', 'lost') },
              ]}
            />
          </div>
        )}
        <DetailModal
          open={funnelDrill.open}
          onClose={() => setFunnelDrill((d) => ({ ...d, open: false }))}
          title={funnelDrill.title}
          loading={funnelDrill.loading}
          rows={funnelDrill.rows}
          metric={funnelDrill.metric}
        />
      </div>

      {/* Priority Today + Sent Contracts — shown side by side so neither takes
          up the whole screen. Each previews a handful of rows with a "View all"
          toggle to expand into a scrollable list. */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
        {/* Priority Today — the activity-driven call list from the buyer portal */}
        <WhoToCallPanel title="Priority Today" />

        {/* Sent Contracts — read receipts for Contracts of Sale emailed to buyers */}
        <SentContractsPanel />
      </div>

      {/* Buyer Enquiries & Offers + New Seller Leads. The old dashboard had
          three parallel new-lead lists because Off/On Market was treated as a
          different data source; now that market is just a badge on the row, the
          two seller lists merge into one, leaving two clean columns instead of
          three cramped ones (final design, Monday 12988442847). */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
        {/* Buyer Enquiries & Offers */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="min-h-[52px] px-4 border-b border-gray-200 flex items-center gap-2.5">
            <h3
              className="text-[13.5px] font-bold text-gray-900 m-0 cursor-pointer hover:underline truncate"
              onClick={() => navigate('/leads')}
            >
              Buyer Enquiries &amp; Offers
            </h3>
            <small className="text-[10.5px] text-gray-400 whitespace-nowrap">{leadTotals.enquiries} uncontacted</small>
            <div className="ml-auto flex items-center gap-2 flex-shrink-0">
              {/* For a buyer who isn't tied to a listing yet — e.g. a call-in
                  enquiry about a suburb and budget, with nowhere else to log it
                  (Paul Jimoulis / Christian Gravias, Monday 12827330408). */}
              <button
                type="button"
                onClick={() => setShowAddBuyer(true)}
                className="text-[11px] font-bold text-gray-600 border border-gray-200 rounded-md px-2 py-1 hover:bg-gray-50 whitespace-nowrap"
              >
                + Add Buyer
              </button>
              <button
                type="button"
                onClick={() => navigate('/leads')}
                className="text-[11.5px] font-extrabold text-[#167f8b] hover:underline whitespace-nowrap"
              >
                View all →
              </button>
            </div>
          </div>
          <div className="p-1.5 max-h-[300px] overflow-y-auto no-scrollbar">
            {buyerEnquiries.length > 0 ? (
              buyerEnquiries.slice(0, 10).map((entry) => (
                <div
                  key={entry.id}
                  onClick={() => navigate(entry.lead_type === 'offer' ? '/leads?tab=offers' : '/leads')}
                  className="px-2.5 py-2.5 flex items-center gap-2.5 border-b border-gray-100 last:border-b-0 cursor-pointer rounded-lg hover:bg-[#f8fdfe] transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <strong className="block text-xs font-bold text-gray-900 truncate">{entry.full_name || 'No Name'}</strong>
                    <small className="block text-[10.5px] text-gray-400 truncate mt-0.5">
                      {entry.lead_type === 'offer' ? 'Offer enquiry' : 'Inspection / buyer enquiry'}
                    </small>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <LeadSourceBadge source={entry.source} />
                    <span className="px-2 py-0.5 bg-amber-50 text-amber-700 text-[9.5px] rounded-full font-extrabold whitespace-nowrap">
                      {entry.stage && entry.stage !== 'Unassessed' ? entry.stage : 'Unaccessed'}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <div className="p-4 text-center text-xs text-gray-400">No enquiries or offers found</div>
            )}
          </div>
        </div>

        {/* New Seller Leads: off market and on market in one list, each row
            badged with where it came from and which market it belongs to. The
            header and rows open Prospects, where these leads are the New and
            Connected columns (Monday 12988442847). */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="min-h-[52px] px-4 border-b border-gray-200 flex items-center gap-2.5">
            <h3
              className="text-[13.5px] font-bold text-gray-900 m-0 cursor-pointer hover:underline truncate"
              onClick={() => navigate('/prospects')}
            >
              New Seller Leads
            </h3>
            <small className="text-[10.5px] text-gray-400 whitespace-nowrap">
              {leadTotals.off_market + leadTotals.on_market} awaiting contact
            </small>
            <div className="ml-auto flex items-center gap-2 flex-shrink-0">
              <button
                type="button"
                onClick={() => setAddLeadMarket('off_market')}
                className="text-[11px] font-bold text-gray-600 border border-gray-200 rounded-md px-2 py-1 hover:bg-gray-50 whitespace-nowrap"
              >
                + Add Lead
              </button>
              <button
                type="button"
                onClick={() => navigate('/prospects')}
                className="text-[11.5px] font-extrabold text-[#167f8b] hover:underline whitespace-nowrap"
              >
                View all →
              </button>
            </div>
          </div>
          <div className="p-1.5 max-h-[300px] overflow-y-auto no-scrollbar">
            {newSellerLeads.length > 0 ? (
              newSellerLeads.map((entry) => (
                <div
                  key={entry.market + '-' + entry.id}
                  onClick={() => navigate('/prospects?market=' + entry.market)}
                  className="px-2.5 py-2.5 flex items-center gap-2.5 border-b border-gray-100 last:border-b-0 cursor-pointer rounded-lg hover:bg-[#f8fdfe] transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <strong className="block text-xs font-bold text-gray-900 truncate">{entry.name || entry.full_name || 'No Name'}</strong>
                    {(entry.property_address || entry.owner_subject_address) && (
                      <small className="block text-[10.5px] text-gray-400 truncate mt-0.5">
                        {formatAddress(entry.property_address || entry.owner_subject_address, { short: true })}
                      </small>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {/* Write up the call without leaving the dashboard. stopPropagation
                        or the row navigates to Seller Leads underneath the modal. */}
                    <button
                      type="button"
                      title="Notes"
                      aria-label={'Notes for ' + (entry.name || entry.full_name || 'this lead')}
                      onClick={(e) => { e.stopPropagation(); setNotesLead(entry); }}
                      className="flex items-center gap-0.5 text-gray-300 hover:text-[#55C5D0] transition-colors"
                    >
                      <StickyNote className="w-3.5 h-3.5" />
                      {noteCountOf(entry) > 0 && (
                        <span className="text-[9.5px] font-bold">{noteCountOf(entry)}</span>
                      )}
                    </button>
                    <span className="px-2 py-0.5 bg-gray-100 text-gray-500 text-[9.5px] rounded-full font-extrabold whitespace-nowrap">
                      {entry.market === 'on_market' ? 'On market' : 'Off market'}
                    </span>
                    <LeadSourceBadge source={entry.source} />
                  </div>
                </div>
              ))
            ) : (
              <div className="p-4 text-center text-xs text-gray-400">No seller leads waiting</div>
            )}
          </div>
        </div>
      </div>

      {/* Market Signal Alerts: Homer-fed new listings and stalling competitor
          listings across the patch (Christian's Growth OS mockup, Monday
          12573204628). Four tiles in a 2x2 grid; the 4th, New Developments,
          shows BCI projects (Monday 12899126094), locked until BCI is set up. */}
      <MarketSignalAlertsPanel />

      {/* My Performance — the agent's own snapshot with a per-metric Rank #N and
          a bar scaled to the team's top value, plus a link into the full
          breakdown. Sits under Who-to-Call, above the lead cards (dashboard
          mockup). Agents only; hidden when there's no row. */}
      {isAgentUser(user) && myPerf?.agent && (
        <div>
          <div className="flex items-center justify-between gap-3 flex-wrap mb-2.5 px-0.5">
            <div className="flex items-center gap-2">
              <span className="bg-[#128f89] text-white text-[9.5px] font-bold px-1.5 py-0.5 rounded">NEW</span>
              <h2 className="font-bold text-sm sm:text-base text-gray-900">My Performance</h2>
            </div>
            <button
              onClick={() => navigate('/my-performance')}
              className="text-[12px] sm:text-[13px] font-semibold text-[#128f89] hover:underline"
            >
              View full breakdown →
            </button>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            <MyPerfTile
              icon="🏠" color="#3d5afe" label="Current listings" sub="active now"
              value={myPerf.agent.current_listings}
              rank={myPerf.agent.metric_ranks?.current_listings}
              max={myPerf.agent.metric_maxes?.current_listings}
              onClick={() => navigate('/my-performance?section=listings')}
            />
            <MyPerfTile
              icon="💰" color="#0d6e6a" label="FY GCI"
              display={fmtGci(myPerf.agent.total_commission)}
              value={myPerf.agent.total_commission}
              target={myPerf.agent.fy_commission_target}
              rank={myPerf.agent.fy_commission_target ? undefined : myPerf.agent.metric_ranks?.total_commission}
              max={myPerf.agent.metric_maxes?.total_commission}
              onClick={() => navigate('/my-performance?section=value')}
            />
            <MyPerfTile
              icon="✓" color="#1a8f56" label="Sold this FY" sub=" "
              value={myPerf.agent.properties_sold}
              rank={myPerf.agent.metric_ranks?.properties_sold}
              max={myPerf.agent.metric_maxes?.properties_sold}
              onClick={() => navigate('/my-performance?section=value')}
            />
            <MyPerfTile
              icon="📋" color="#e0921a" label="Appraisals"
              sub={`${myPerf.agent.agent_appraisals ?? 0} Own · ${myPerf.agent.collings_appraisals ?? 0} Collings`}
              value={myPerf.agent.appraisals}
              target={myPerf.agent.fy_appraisal_target}
              rank={myPerf.agent.fy_appraisal_target ? undefined : myPerf.agent.metric_ranks?.appraisals}
              max={myPerf.agent.metric_maxes?.appraisals}
              onClick={() => navigate('/my-performance?section=appraisals')}
            />
            <MyPerfTile
              icon="👤" color="#4b4fd6" label="Collings leads"
              sub={`${myPerf.agent.collings_contacted ?? 0} actioned · ${myPerf.agent.collings_never_contacted ?? 0} need action`}
              alert={(myPerf.agent.collings_never_contacted ?? 0) > 0}
              value={myPerf.agent.collings_leads}
              onClick={() => navigate('/my-performance?section=collings')}
            />
          </div>
        </div>
      )}

      {/* Calendar & Tasks Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Calendar */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 h-[338px] flex flex-col">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-gray-800 cursor-pointer hover:underline" onClick={() => navigate('/calendar')}>Calendar</h3>
            <div className="flex space-x-1">
              <button
                onClick={() => setCalendarView('Day')}
                className={`px-3 py-1 text-xs rounded transition-colors ${calendarView === 'Day' ? 'text-teal-600 bg-teal-50 font-medium' : 'text-gray-500 hover:bg-gray-100'}`}
              >
                Day
              </button>
              <button
                onClick={() => setCalendarView('Week')}
                className={`px-3 py-1 text-xs rounded transition-colors ${calendarView === 'Week' ? 'text-teal-600 bg-teal-50 font-medium' : 'text-gray-500 hover:bg-gray-100'}`}
              >
                Week
              </button>
              <button
                onClick={() => setCalendarView('Month')}
                className={`px-3 py-1 text-xs rounded transition-colors ${calendarView === 'Month' ? 'text-teal-600 bg-teal-50 font-medium' : 'text-gray-500 hover:bg-gray-100'}`}
              >
                Month
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto no-scrollbar">
            <div className="w-full">
              {calendarView === 'Month' ? (
                <div className="pt-2 pb-2 px-1">
                  <div className="grid grid-cols-7 gap-0 mb-3 border-b border-gray-100 pb-2">
                    {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((d, i) => (
                      <div key={i} className="text-center text-[10px] font-bold text-gray-400 uppercase tracking-wider">{d}</div>
                    ))}
                  </div>
                  <div className="grid grid-cols-7 gap-2">
                    {monthDays.map((day, idx) => (
                      <div
                        key={idx}
                        className={`aspect-square flex items-center justify-center text-xs rounded-lg transition-all duration-200
                          ${day === new Date().getDate() ? 'bg-[#55C5D0] text-white font-bold shadow-sm scale-110' : day ? 'text-gray-700 hover:bg-gray-50 border border-transparent hover:border-gray-100' : ''}`}
                      >
                        {day}
                      </div>
                    ))}
                  </div>
                  <div className="mt-6 space-y-3">
                    {inspections.length > 0 ? (
                      inspections.slice(0, 5).map((inspection) => {
                        // Melbourne wall-clock, not the viewer's browser zone.
                        const isToday = formatMelbourneDatePart(inspection.scheduled_date) === formatMelbourneDatePart(new Date());
                        const timeStr = formatMelbourneClock(inspection.scheduled_date);
                        const dateStr = isToday
                          ? 'Today'
                          : new Intl.DateTimeFormat('en-US', { timeZone: MELBOURNE_TZ, month: 'short', day: 'numeric' }).format(new Date(inspection.scheduled_date));

                        return (
                          <div
                            key={inspection.id}
                            onClick={() => navigate(`/inspections`)}
                            className="flex items-center space-x-3 p-3 bg-cyan-50/50 rounded-xl border border-cyan-100/50 transition-colors hover:bg-cyan-50 cursor-pointer"
                          >
                            <div className="w-2 h-2 bg-[#55C5D0] rounded-full shadow-sm"></div>
                            <div className="flex-1 min-w-0">
                              <div className="text-[11px] text-cyan-900 font-bold mb-0.5 truncate">
                                {formatAddress(inspection.property_address || inspection.property_title || 'Inspection', { short: true })}
                              </div>
                              <div className="text-[10px] text-cyan-700">{dateStr} • {timeStr}</div>
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <div className="text-center py-4 text-gray-400 text-xs">
                        No inspections scheduled
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <>
                  {/* Header Days */}
                  <div className={`grid ${calendarView === 'Week' ? 'grid-cols-8' : 'grid-cols-2'} gap-0 mb-3`}>
                    <div className="col-span-1"></div>
                    {calendarView === 'Week' ? (
                      weekDays.map((day, idx) => (
                        <div key={idx} className="text-center">
                          <div className={`text-xs font-bold transition-colors ${day.date === new Date().getDate() ? 'text-[#55C5D0]' : 'text-gray-700'}`}>{day.day}</div>
                          <div className="text-[10px] text-gray-500 font-medium">{day.date}</div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center bg-teal-50/50 py-1 rounded-lg border border-teal-100/50">
                        <div className="text-xs font-bold text-[#55C5D0] tracking-tight">{new Date().toLocaleDateString('en-US', { weekday: 'long' })}</div>
                        <div className="text-[10px] text-gray-500 font-medium">{new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}</div>
                      </div>
                    )}
                  </div>

                  {/* Time Slots Grid */}
                  <div className="relative">
                    {[10, 11, 12, 1, 2, 3, 4].map((hour, idx) => (
                      <div key={idx} className={`grid ${calendarView === 'Week' ? 'grid-cols-8' : 'grid-cols-2'} gap-0 h-16 border-t border-gray-200`}>
                        <div className="col-span-1 text-xs text-gray-400 font-medium pt-1 px-1">
                          {hour}:00
                        </div>
                        {calendarView === 'Week' ? (
                          weekDays.map((_, dayIdx) => (
                            <div key={dayIdx} className="border-l border-gray-200 h-full relative group">
                              <div className="absolute inset-0 bg-teal-50/0 group-hover:bg-teal-50/20 transition-colors" />
                            </div>
                          ))
                        ) : (
                          <div className="border-l border-gray-200 h-full relative group">
                            <div className="absolute inset-0 bg-teal-50/0 group-hover:bg-teal-50/20 transition-colors" />
                          </div>
                        )}
                      </div>
                    ))}

                    {/* Events Overlay */}
                    {inspections.length > 0 && (
                      <>
                        {calendarView === 'Week' ? (
                          inspections.slice(0, 3).map((inspection, idx) => {
                            const inspectionDate = moment(inspection.scheduled_date);
                            const dayOfWeek = inspectionDate.day(); // 0 = Sunday, 1 = Monday, etc.
                            const adjustedDay = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Convert to Monday=0, Sunday=6
                            // Vertical (time-of-day) position in Melbourne wall-clock time.
                            const { hour, minute } = melbourneHourMinute(inspection.scheduled_date);
                            const hourIndex = hour >= 10 ? hour - 10 : 0;
                            const topOffset = hourIndex * 64 + (minute / 60) * 64 + 4;
                            const leftOffset = `calc(12.5% * ${adjustedDay + 1} + 2px)`;

                            return (
                              <div
                                key={inspection.id}
                                onClick={() => navigate(`/inspections`)}
                                className="absolute z-10"
                                style={{
                                  top: `${topOffset}px`,
                                  left: leftOffset,
                                  width: 'calc(12.5% - 4px)',
                                  height: '54px'
                                }}
                              >
                                <div className="bg-[#E0F7FA] p-2 text-[10px] rounded-md h-full flex flex-col justify-center shadow-sm border-l-2 border-[#55C5D0] transition-transform hover:scale-[1.02] cursor-pointer">
                                  <div className="font-bold text-cyan-900 mb-0.5">{formatMelbourneClock(inspection.scheduled_date)}</div>
                                   <div className="text-cyan-800 leading-tight font-medium truncate">
                                    {formatAddress(inspection.property_address || inspection.property_title || 'Inspection', { short: true })}
                                  </div>
                                </div>
                              </div>
                            );
                          })
                        ) : (
                          inspections.slice(0, 1).map((inspection) => {
                            // Vertical (time-of-day) position in Melbourne wall-clock time.
                            const { hour, minute } = melbourneHourMinute(inspection.scheduled_date);
                            const hourIndex = hour >= 10 ? hour - 10 : 0;
                            const topOffset = hourIndex * 64 + (minute / 60) * 64 + 4;

                            return (
                              <div
                                key={inspection.id}
                                onClick={() => navigate(`/inspections`)}
                                className="absolute z-10"
                                style={{
                                  top: `${topOffset}px`,
                                  left: 'calc(50% + 2px)',
                                  width: 'calc(50% - 4px)',
                                  height: '54px'
                                }}
                              >
                                <div className="bg-[#E0F7FA] p-3 text-[10px] rounded-xl h-full flex flex-col justify-center shadow-md border-l-4 border-[#55C5D0] transition-transform hover:scale-[1.02] cursor-pointer">
                                  <div className="font-bold text-cyan-900 mb-1">{formatMelbourneClock(inspection.scheduled_date)}</div>
                                   <div className="text-cyan-800 text-[11px] font-bold tracking-tight truncate">
                                    {formatAddress(inspection.property_address || inspection.property_title || 'Inspection', { short: true })}
                                  </div>
                                  {inspection.property_address && (
                                    <div className="text-cyan-600 text-[9px] mt-0.5 font-medium truncate">
                                      {formatAddress(inspection.property_address, { short: true })}
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })
                        )}
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Tasks & Reminders */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 flex flex-col h-[338px]">
          <div className="flex justify-between items-center mb-4">
            <div className="min-w-0">
              <h3 className="font-bold text-gray-800 cursor-pointer hover:underline" onClick={() => navigate('/tasks')}>Tasks & Reminders</h3>
              <p className="text-[10px] text-gray-400">Due today</p>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0">
              <button
                onClick={() => setShowFutureReminders(true)}
                className="text-xs text-teal-600 hover:text-teal-700 font-medium flex items-center gap-1"
              >
                <Calendar className="w-3.5 h-3.5" />
                Future Reminders
              </button>
              <button
                onClick={() => navigate('/tasks')}
                className="text-xs text-teal-600 hover:text-teal-700 font-medium"
              >
                View All
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto no-scrollbar">
            {tasks.length > 0 ? (
              <div className="space-y-3">
                {tasks.slice(0, 5).map((task) => (
                  <div
                    key={task.id}
                    className="flex items-start space-x-3 p-3 rounded-lg border border-gray-50 hover:border-teal-100 hover:bg-teal-50/30 transition-all cursor-pointer group"
                    onClick={() => navigate('/tasks')}
                  >
                    <div className={`mt-0.5 w-2 h-2 rounded-full flex-shrink-0 ${task.priority === 'high' ? 'bg-red-500' :
                      task.priority === 'medium' ? 'bg-orange-400' : 'bg-green-400'
                      }`} />
                    <div className="flex-1 min-w-0">
                      {(task.contact_name || task.lead_name) ? (
                        <>
                          <div className="text-sm font-bold text-gray-900 truncate group-hover:text-teal-700">{task.contact_name || task.lead_name}</div>
                          <div className="text-[11px] text-gray-500 truncate">{task.task_name}</div>
                        </>
                      ) : (
                        <div className="text-xs font-bold text-gray-900 truncate group-hover:text-teal-700">{task.task_name}</div>
                      )}
                      {task.property_address && (
                        <div className="text-[10px] text-gray-500 truncate mt-0.5">{formatAddress(task.property_address, { short: true })}</div>
                      )}
                      {task.latest_note?.text && (
                        <div className="flex items-center gap-1 text-[10px] text-amber-600 truncate mt-0.5">
                          <StickyNote className="w-2.5 h-2.5 flex-shrink-0" />
                          <span className="truncate">{task.latest_note.text}</span>
                        </div>
                      )}
                      {task.due_date && (
                        <div className="flex items-center mt-1 text-[10px] text-gray-500">
                          <Clock className="w-3 h-3 mr-1" />
                          <span>{formatDateAU(task.due_date)}</span>
                        </div>
                      )}
                    </div>
                    <ArrowRight className="w-4 h-4 text-gray-300 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center text-gray-300 min-h-[200px]">
                <CheckCircle className="w-16 h-16 mb-2 stroke-1" />
                <span className="text-sm">No tasks due today</span>
                <button
                  onClick={() => setShowFutureReminders(true)}
                  className="mt-2 text-xs text-teal-600 hover:text-teal-700 font-medium"
                >
                  View future reminders
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Listings & News Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Current Listings */}
        <div className="bg-white rounded-lg border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-2">
              <Home className="w-5 h-5 text-teal-500" />
              <h3 className="font-bold text-gray-800 text-base">Current Listings - Days on Market</h3>
            </div>
            <button
              onClick={() => navigate('/properties')}
              className="text-sm text-teal-500 hover:text-teal-600 font-medium"
            >
              View All
            </button>
          </div>
          <div className="space-y-3 max-h-[400px] overflow-y-auto no-scrollbar pr-1">
            {reaCurrentListings.length > 0 ? (
              reaCurrentListings.slice(0, 10).map((listing) => {
                // Use pre-calculated days if available, or calculate (fallback)
                const days = listing.days !== undefined ? listing.days : (listing.created_at
                  ? Math.floor((new Date() - new Date(listing.created_at)) / (1000 * 60 * 60 * 24))
                  : 0);

                return (
                  <div
                    key={listing.id}
                    onClick={() => navigate(`/properties/${listing.id}`)}
                    className="flex items-center justify-between p-4 border border-gray-100 rounded-xl hover:shadow-md transition-all cursor-pointer bg-white"
                  >
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 rounded-xl bg-cyan-50 flex items-center justify-center flex-shrink-0">
                        {listing.image ? (
                          <img src={listing.image} alt="" className="w-full h-full object-cover rounded-xl" />
                        ) : (
                          <MapPin className="w-6 h-6 text-cyan-500" />
                        )}
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-bold text-gray-900 truncate max-w-[200px] sm:max-w-[250px] mb-0.5">
                          {formatAddress(listing.address || listing.headline || 'Unknown Address', { short: true })}
                        </div>
                        <div className="text-xs text-gray-400 font-light">Active listing</div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-4 flex-shrink-0">
                      <div className="text-right">
                        <div className={`text-xl font-bold ${days > 30 ? 'text-red-500' : 'text-green-500'}`}>
                          {days}
                        </div>
                        <div className="text-[10px] text-gray-400">days</div>
                      </div>
                      <ArrowRight className="w-5 h-5 text-gray-300" />
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="py-8 text-center text-gray-500 text-sm">No active listings found</div>
            )}
          </div>
        </div>

        {/* Real Estate News */}
        <div className="bg-white rounded-lg border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-2">
              <Newspaper className="w-5 h-5 text-teal-500" />
              <h3 className="font-bold text-gray-800 text-base">Real Estate News</h3>
            </div>
            <button
              onClick={() => window.open('https://www.realestate.com.au/news/', '_blank')}
              className="text-sm text-teal-500 hover:text-teal-600 font-medium"
            >
              View All
            </button>
          </div>
          <div className="space-y-3 max-h-[400px] overflow-y-auto no-scrollbar pr-1">
            {newsArticles.length === 0 && (
              <div className="text-center text-sm text-gray-400 py-8">No news right now — check back soon.</div>
            )}
            {newsArticles.map((article, idx) => (
              <a
                key={idx}
                href={article.link}
                target="_blank"
                rel="noopener noreferrer"
                className="flex gap-4 group hover:shadow-md border border-gray-100 p-3 rounded-xl transition-all cursor-pointer bg-white"
              >
                {article.image ? (
                  <img
                    src={article.image}
                    alt=""
                    className="w-24 h-20 rounded-lg object-cover flex-shrink-0"
                    onError={(e) => { e.currentTarget.remove(); }}
                  />
                ) : (
                  <div className="w-24 h-20 rounded-lg bg-teal-50 flex items-center justify-center flex-shrink-0">
                    <Newspaper className="w-6 h-6 text-teal-400" />
                  </div>
                )}
                <div className="flex-1 min-w-0 flex flex-col justify-between py-0.5">
                  <div>
                    <div className="flex justify-between items-start gap-2">
                      <h4 className="text-sm font-bold text-gray-900 leading-tight mb-1 group-hover:text-teal-600 transition-colors line-clamp-1">{article.title}</h4>
                      <ExternalLink className="w-4 h-4 text-gray-400 flex-shrink-0 group-hover:text-teal-500" />
                    </div>
                    <p className="text-xs text-gray-500 leading-relaxed line-clamp-1">{article.description}</p>
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-xs text-teal-500 font-medium">{article.source}</span>
                    <div className="flex items-center text-xs text-gray-400">
                      <Clock className="w-3.5 h-3.5 mr-1.5" />
                      {article.date}
                    </div>
                  </div>
                </div>
              </a>
            ))}
          </div>
        </div>
      </div>

      {/* Future Reminders — calendar of reminders scheduled for an upcoming day */}
      <FutureRemindersModal
        open={showFutureReminders}
        onClose={() => setShowFutureReminders(false)}
      />

      {/* "Add Lead" on the Off / On Market Leads widgets — lets an agent type a
          seller lead straight in from the dashboard and hand it to a colleague. */}
      <AddSellerLeadModal
        isOpen={!!addLeadMarket}
        defaultMarket={addLeadMarket || 'off_market'}
        onClose={() => setAddLeadMarket(null)}
        onSaved={loadSellerLeads}
      />

      {/* "Add Buyer" on the Enquiries / Offers widget — a buyer with brief
          preferences but no property to assign them to yet. */}
      {showAddBuyer && (
        <AddBuyerModal onClose={() => setShowAddBuyer(false)} />
      )}

      {/* Notes on a seller lead, straight off the widget. Same thread the Seller
          Leads page and the Buyer Enquiries modal read. */}
      <LeadNotesModal
        isOpen={!!notesLead}
        lead={notesLead}
        onClose={() => setNotesLead(null)}
        onSaved={handleLeadNoteSaved}
      />
    </div>
  );
};

// One property row in the search dropdown. Shared by the Properties group and
// the Public Properties group below the contacts, so a Database row reads
// exactly like a listing apart from its indigo marker.
const SearchPropertyRow = ({ property, onClick, formatPrice }) => {
  // Prefer the real address over a marketing headline/title.
  const baseAddress = formatAddress(property.address || property.title || 'Untitled Property', { short: true });
  // "24 Renown Street, Coburg North": one comma, suburb in Title Case.
  const fullAddress = joinStreetAndSuburb(baseAddress, property.suburb);

  // A Database property - the layer below appraisals and listings
  // (Christian Gravias, CRM-2026-00031).
  const isPublicProperty = property.source === 'public';

  return (
    <div
      onClick={() => onClick(property)}
      className={`px-3 py-2.5 rounded-lg cursor-pointer transition-colors group ${isPublicProperty ? 'hover:bg-indigo-50' : 'hover:bg-teal-50'}`}
    >
      <div className="flex items-start gap-3">
        <div className={`mt-0.5 p-1.5 rounded transition-colors ${isPublicProperty
          ? 'bg-indigo-100 group-hover:bg-indigo-200'
          : 'bg-teal-100 group-hover:bg-teal-200'}`}>
          <Building className={`w-4 h-4 ${isPublicProperty ? 'text-indigo-600' : 'text-teal-600'}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className={`font-bold text-sm text-gray-900 flex items-center gap-2 flex-wrap ${isPublicProperty ? 'group-hover:text-indigo-700' : 'group-hover:text-teal-700'}`}>
            <span>{fullAddress}</span>
            {isPublicProperty && (
              <span
                title="A property profile built from your Database. One layer below your appraisals and listings."
                className="px-2 py-0.5 rounded bg-indigo-100 text-indigo-700 text-[10px] font-semibold uppercase tracking-wide"
              >
                Public Property
              </span>
            )}
            {/* An address only the office's spreadsheets hold (Monday 13354971743). */}
            {property.owner_on_file && (
              <span className="px-2 py-0.5 rounded bg-teal-100 text-teal-700 text-[10px] font-semibold uppercase tracking-wide">
                Owner on file
              </span>
            )}
            {property.status && ['Withdrawn', 'Sold', 'Archived'].includes(property.status) && (
              <span className={`px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide ${
                property.status === 'Sold'
                  ? 'bg-green-100 text-green-700'
                  : property.status === 'Withdrawn'
                    ? 'bg-orange-100 text-orange-700'
                    : 'bg-gray-200 text-gray-700'
              }`}>
                {property.status}
              </span>
            )}
          </div>
          <div className="flex items-center gap-3 mt-1 text-xs text-gray-600">
            {property.price > 0 && (
              <span className="font-semibold text-teal-600">{formatPrice(property.price)}</span>
            )}
            {property.beds ? <span>{property.beds} bed</span> : null}
            {property.baths ? <span>{property.baths} bath</span> : null}
            {property.source === 'collings' && (
              <span className="text-[10px] text-gray-400">Collings</span>
            )}
          </div>
          {/* A sale and a rental of this address: one row, a button for each
              (Christian Gravias, Monday 13245253503, 74 Derby Street). */}
          {property.campaigns?.length > 1 && (
            <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
              {property.campaigns.map((c) => (
                <button
                  key={`${c.source}-${c.id}`}
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onClick({ ...property, url: c.url });
                  }}
                  className="px-2 py-0.5 rounded border border-teal-200 bg-white text-[10px] font-semibold text-teal-700 hover:bg-teal-600 hover:text-white hover:border-teal-600 transition-colors"
                >
                  {c.kind === 'rental' ? 'Rental' : 'Sale'}
                  {c.price > 0 ? ` · ${formatPrice(c.price)}` : ''}
                  {['Sold', 'Withdrawn'].includes(c.status) ? ` · ${c.status}` : ''}
                </button>
              ))}
            </div>
          )}
        </div>
        <ArrowRight className="w-4 h-4 text-gray-300 group-hover:text-teal-500 opacity-0 group-hover:opacity-100 transition-all" />
      </div>
    </div>
  );
};

export default DashboardPage;
